/**
 * Host parsing for subdomain-based multi-tenancy.
 *
 * Splits an incoming HTTP `Host` header into either:
 *
 *   - the apex base host (e.g. `local.solution.co.uk`), meaning the request
 *     targets the shared global-admin / marketing surface; OR
 *   - a tenant subdomain + base host (e.g. `penny` + `local.solution.co.uk`),
 *     meaning the request targets a specific organisation.
 *
 * The `baseHosts` argument is the list of environment hostnames we recognise
 * as apex domains (`solution.co.uk`, `uat.solution.co.uk`, `dev.solution.co.uk`,
 * `local.solution.co.uk`). Anything to the LEFT of a matching base host is
 * treated as the tenant DNS label; requests to bases we don't recognise
 * (e.g. `localhost:3000`, a preview deploy, or an IP) are returned as apex
 * so they behave exactly like today.
 *
 * Reserved DNS labels (see `lib/org/reservedSlugs.ts`) are NOT considered
 * tenant subdomains — `www.solution.co.uk`, `api.solution.co.uk`, etc. resolve
 * back to apex, matching the invariant enforced by the org-creation admission
 * gate (`isAllowedOrgSlug`).
 */

import { isReservedSubdomain } from '@/lib/org/reservedSlugs';

export interface ParsedHost {
  /** The tenant subdomain if the request targets one, else `null`. */
  subdomain: string | null;
  /** The matched base host (without port). Empty when unrecognised. */
  baseHost: string;
  /** True when the request targets the apex (no tenant subdomain). */
  isApex: boolean;
  /** True when a valid, non-reserved tenant subdomain was extracted. */
  isTenantSubdomain: boolean;
}

/**
 * Parse a `Host` header value against the environment's recognised base
 * hosts. Ports are stripped; comparison is case-insensitive; IPv6 hosts
 * (`[::1]:3000`) and IPv4 hosts are treated as apex.
 */
export function parseHost(
  host: string | null | undefined,
  baseHosts: readonly string[],
): ParsedHost {
  const empty: ParsedHost = {
    subdomain: null,
    baseHost: '',
    isApex: true,
    isTenantSubdomain: false,
  };

  if (!host || typeof host !== 'string') return empty;

  // Strip port. IPv6 hosts arrive as `[::1]:3000`; extract everything up to
  // the closing bracket first, otherwise split on the last colon.
  let hostname: string;
  if (host.startsWith('[')) {
    const end = host.indexOf(']');
    hostname = end === -1 ? host : host.slice(1, end);
  } else {
    const colon = host.lastIndexOf(':');
    hostname = colon === -1 ? host : host.slice(0, colon);
  }
  hostname = hostname.toLowerCase().trim();
  if (!hostname) return empty;

  // Numeric IPs (including embedded IPv6) never carry a tenant subdomain.
  if (isNumericHost(hostname)) {
    return { subdomain: null, baseHost: hostname, isApex: true, isTenantSubdomain: false };
  }

  // Longest match wins so `local.solution.co.uk` beats `solution.co.uk`
  // when both are configured (production + local share the same suffix).
  const sorted = [...baseHosts]
    .filter((b) => typeof b === 'string' && b.length > 0)
    .map((b) => b.toLowerCase())
    .sort((a, b) => b.length - a.length);

  for (const base of sorted) {
    if (hostname === base) {
      return { subdomain: null, baseHost: base, isApex: true, isTenantSubdomain: false };
    }
    // Exact-suffix match with a `.` separator so `not-solution.co.uk` does
    // not accidentally match `solution.co.uk`.
    if (hostname.endsWith(`.${base}`)) {
      const label = hostname.slice(0, -1 * (base.length + 1));
      // Multi-level subdomains (e.g. `foo.penny.local.solution.co.uk`) are
      // intentionally NOT supported: our wildcard TLS cert covers only one
      // level of subdomain (`*.solution.co.uk` matches `penny.solution.co.uk`
      // but NOT `foo.penny.solution.co.uk`). Anything with an embedded dot
      // falls back to apex so it can't accidentally route to a tenant.
      if (!label || label.includes('.')) {
        return { subdomain: null, baseHost: base, isApex: true, isTenantSubdomain: false };
      }
      if (isReservedSubdomain(label)) {
        return { subdomain: null, baseHost: base, isApex: true, isTenantSubdomain: false };
      }
      return {
        subdomain: label,
        baseHost: base,
        isApex: false,
        isTenantSubdomain: true,
      };
    }
  }

  // Host wasn't on the recognised list (e.g. `localhost`, preview URL).
  // Behave like apex so today's path-based routing keeps working.
  return { subdomain: null, baseHost: hostname, isApex: true, isTenantSubdomain: false };
}

/**
 * Load the list of environment base hosts from an env var (comma-separated),
 * falling back to the four known Penny environments. Kept as a plain string
 * env var so it can be overridden per deployment without a code change.
 */
export function getConfiguredBaseHosts(): string[] {
  const raw = process.env.NEXT_PUBLIC_APP_BASE_HOSTS?.trim();
  if (raw) {
    return raw
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter((s) => s.length > 0);
  }
  return DEFAULT_BASE_HOSTS.slice();
}

/**
 * The known Penny environment hostnames. Kept in one place so tests and
 * runtime consumers share the same defaults.
 */
export const DEFAULT_BASE_HOSTS: readonly string[] = Object.freeze([
  'solution.co.uk',
  'uat.solution.co.uk',
  'dev.solution.co.uk',
  'local.solution.co.uk',
]);

/**
 * Returns `true` for a bare IPv4 or IPv6 literal.
 */
function isNumericHost(hostname: string): boolean {
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)) return true;
  if (/^[0-9a-f:]+$/.test(hostname) && hostname.includes(':')) return true;
  return false;
}
